/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { useState, useRef, useCallback, useEffect } from 'react';
import type { AddressSuggestion } from '@/components/address-suggestion-dropdown';

export const MIN_INPUT_LENGTH = 3;
export const MAX_SUGGESTIONS = 5;

const SEARCH_URL = '/resource/experian/address/search';
const FORMAT_URL_PREFIX = '/resource/experian/address/format/';

export interface ExperianAddressFields {
    address1: string;
    address2?: string;
    city?: string;
    stateCode?: string;
    postalCode?: string;
}

export interface UseExperianAddressSuggestionsResult {
    suggestions: AddressSuggestion[];
    isLoading: boolean;
    tooManyMatches: boolean;
    resetSession: () => void;
    fetchAddressFormat: (globalAddressKey: string) => Promise<ExperianAddressFields | null>;
}

/**
 * Converts Experian suggestion to AddressSuggestion format for the dropdown.
 */
function toAddressSuggestion(text: string, globalAddressKey: string, confidence?: string): AddressSuggestion {
    return {
        description: text,
        place_id: globalAddressKey,
        global_address_key: globalAddressKey,
        structured_formatting: { main_text: text },
        terms: [{ value: text }],
        confidence,
    };
}

/** Country code to Experian country_iso and dataset mapping (accepts US/USA, CA/CAN) */
const COUNTRY_CONFIG: Record<string, { iso: string; dataset: string }> = {
    US: { iso: 'USA', dataset: 'us-address' },
    USA: { iso: 'USA', dataset: 'us-address' },
    CA: { iso: 'CAN', dataset: 'ca-address' },
    CAN: { iso: 'CAN', dataset: 'ca-address' },
};

/**
 * Hook for Experian Aperture address autocomplete.
 * Triggers search after 3rd character, uses AbortController to cancel in-flight requests,
 * returns max 5 suggestions. Handles "Too many matches" confidence.
 * @param countryCode - US/USA or CA/CAN (from config or form)
 */
export function useExperianAddressSuggestions(
    inputString: string,
    enabled: boolean,
    countryCode: string = 'US'
): UseExperianAddressSuggestionsResult {
    const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
    const [isLoading, setIsLoading] = useState(false);
    const [tooManyMatches, setTooManyMatches] = useState(false);

    const abortControllerRef = useRef<AbortController | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const resetSession = useCallback(() => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
            abortControllerRef.current = null;
        }
        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
            debounceRef.current = null;
        }
        setSuggestions([]);
        setIsLoading(false);
        setTooManyMatches(false);
    }, []);

    const fetchAddressFormat = useCallback(
        async (globalAddressKey: string): Promise<ExperianAddressFields | null> => {
            if (!enabled) return null;

            try {
                const res = await fetch(`${FORMAT_URL_PREFIX}${encodeURIComponent(globalAddressKey)}`, {
                    method: 'GET',
                    headers: { Accept: 'application/json' },
                });

                if (!res.ok) return null;

                const json = (await res.json()) as {
                    result?: { address?: Record<string, string> };
                };
                const addr = json?.result?.address;
                if (!addr) return null;

                return {
                    address1: addr.address_line_1 ?? '',
                    address2: addr.address_line_2,
                    city: addr.locality,
                    stateCode: addr.region,
                    postalCode: addr.postal_code,
                };
            } catch {
                return null;
            }
        },
        [enabled]
    );

    useEffect(() => {
        if (!enabled || !inputString || inputString.length < MIN_INPUT_LENGTH) {
            setSuggestions([]);
            setTooManyMatches(false);
            return;
        }

        if (debounceRef.current) {
            clearTimeout(debounceRef.current);
        }

        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        debounceRef.current = setTimeout(() => {
            debounceRef.current = null;
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsLoading(true);
            setTooManyMatches(false);

            const { iso, dataset } = COUNTRY_CONFIG[countryCode] ?? COUNTRY_CONFIG.US;
            fetch(SEARCH_URL, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    country_iso: iso,
                    datasets: [dataset],
                    max_suggestions: MAX_SUGGESTIONS,
                    components: { unspecified: [inputString] },
                }),
                signal: controller.signal,
            })
                .then(async (res) => {
                    if (controller.signal.aborted) return;
                    if (!res.ok) {
                        setSuggestions([]);
                        return;
                    }
                    const data = (await res.json()) as {
                        result?: {
                            suggestions?: Array<{ text: string; global_address_key: string; confidence?: string }>;
                        };
                    };
                    const list = data?.result?.suggestions ?? [];

                    if (controller.signal.aborted) return;

                    const hasTooMany = list.some((s) => s.confidence === 'Too many matches');
                    setTooManyMatches(hasTooMany);

                    const mapped = list
                        .filter((s) => s.global_address_key && s.text)
                        .slice(0, MAX_SUGGESTIONS)
                        .map((s) => toAddressSuggestion(s.text, s.global_address_key, s.confidence));

                    setSuggestions(mapped);
                })
                .catch(() => {
                    if (!controller.signal.aborted) {
                        setSuggestions([]);
                    }
                })
                .finally(() => {
                    if (!controller.signal.aborted) {
                        setIsLoading(false);
                    }
                    abortControllerRef.current = null;
                });
        }, 300);

        return () => {
            if (debounceRef.current) {
                clearTimeout(debounceRef.current);
            }
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [inputString, enabled, countryCode]);

    return {
        suggestions,
        isLoading,
        tooManyMatches,
        resetSession,
        fetchAddressFormat,
    };
}
